/**
 * @file Enemy entity factory.
 *
 * One function builds every archetype: it reads a row out of
 * {@link ../config/Enemies.js ENEMIES} and attaches the components that row
 * implies. A ranged row gains a `Weapon`, an explosive row gains `Explosive`.
 * A new enemy is a new row, never a subclass (GDD §9, §23).
 */

import {
  Transform,
  Velocity,
  Health,
  Collider,
  Renderable,
  AIBrain,
  RangedAI,
  Weapon,
  Explosive,
  ExplosiveTrigger
} from '../components/index.js';
import { Layer } from '../config/Layers.js';
import { getEnemyTuning } from '../config/EnemyTuning.js';
import { getWeaponArchetype } from '../config/WeaponTuning.js';
import { Prefab } from '../rendering/Prefabs.js';

/**
 * Enemy guns have endless reserve ammo, but the magazine still empties so
 * reload windows remain part of their combat rhythm.
 * @param {string} weaponKey Key into the weapon tuning table.
 * @returns {Partial<import('../config/Weapons.js').WeaponArchetype>}
 */
function enemyAmmoOverrides(weaponKey) {
  const baseKey = weaponKey.replace(/_enemy$/, '');
  const base = getWeaponArchetype(baseKey);
  const magazineCapacity = base?.magazineCapacity;

  return {
    infiniteAmmo: false,
    infiniteReserve: true,
    reloadEnabled: true,
    autoReloadWhenEmpty: true,
    magazineCapacity,
    startingAmmo: magazineCapacity,
    reserveAmmo: magazineCapacity,
    reloadDuration: base?.reloadDuration,
    reloadPresentation: base?.reloadPresentation ?? { style: 'magazine', finalClack: true }
  };
}

/**
 * Spawns an enemy of the given archetype.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {number} x Spawn X.
 * @param {number} z Spawn Z.
 * @param {string} [archetypeKey] Key into {@link ENEMIES}.
 * @param {object} [options]
 * @param {boolean} [options.idleLife] Whether to start in the archetype's idle state.
 * @param {number} [options.facing] Initial facing (radians). Must be applied to the
 *   `Transform` before the brain/AI component is built — both snapshot it as the
 *   anchor idle states hold, so setting it afterward leaves them facing 0.
 * @returns {import('../ecs/Entity.js').Entity}
 */
export function createEnemy(ctx, x, z, archetypeKey = 'grunt', { idleLife = false, facing = 0 } = {}) {
  // Read through the tuning layer, never `ENEMIES` directly, or a live edit in
  // the debug window's Enemies tab would never reach a spawn (issue #124).
  const archetype = getEnemyTuning()[archetypeKey];
  if (!archetype) throw new ReferenceError(`Unknown enemy archetype "${archetypeKey}".`);

  const { world, meshFactory } = ctx;
  const entity = world.createEntity('enemy');

  const transform = entity.add(new Transform(x, 0, z));
  transform.rotationY = facing;
  entity.add(new Velocity(archetype.speed, archetype.acceleration, archetype.acceleration));
  entity.add(new Health(archetype.maxHealth));

  if (archetype.behavior === 'ranged_template') {
    // Data-driven `enemy_ranged_01` template: one component holds all per-instance
    // AI state, parameters come from the archetype's `ranged` block.
    const weaponKey = archetype.ranged?.enemy_weapon ?? archetype.weapon ?? 'spitter';
    const ai = entity.add(
      new RangedAI({
        config: { ...archetype.ranged, enemy_weapon: weaponKey },
        instanceName: archetypeKey,
        radius: archetype.radius,
        separationRadius: archetype.separationRadius,
        separationStrength: archetype.separationStrength
      })
    );
    ai.anchorTo(x, z, transform.rotationY);
    entity.add(Weapon.fromArchetype(weaponKey, enemyAmmoOverrides(weaponKey)).equip(entity));
  } else {
    const brain = new AIBrain({
      name: `${archetype.name}#${entity.id}`,
      behavior: archetype.behavior,
      radius: archetype.radius,
      seekRange: archetype.seekRange,
      attackRange: archetype.attackRange,
      preferredRange: archetype.preferredRange,
      attackRate: archetype.attackRate,
      damage: archetype.damage,
      separationRadius: archetype.separationRadius,
      separationStrength: archetype.separationStrength,
      // Idle-life extensions (undefined rows fall back to safe defaults).
      // Wave spawns are combat reinforcements and skip the idle starter — they
      // hunt immediately, exactly as before. Bombers keep their idle machine
      // everywhere: an instantly-charging bomber reads as a bug, not a threat.
      starterState:
        idleLife || archetype.explosiveTrigger ? archetype.starterState : undefined,
      detection: archetype.detection,
      aggro: archetype.aggro,
      idle: archetype.idle,
      alertRadius: archetype.alertRadius,
      retreatHealthRatio: archetype.retreatHealthRatio,
      hearingRadius: archetype.hearingRadius,
      leader: archetype.leader
    });
    // Anchor idle behaviours to the spawn point and freeze the initial facing.
    // Also seed stuck-detection samples so a `starter_state` of AGGRO_CHARGE does
    // not measure progress against the origin (enter hooks only run on transitions).
    brain.spawnX = x;
    brain.spawnZ = z;
    brain.stuckSampleX = x;
    brain.stuckSampleZ = z;
    brain.spawnRotationY = facing;
    entity.add(brain);

    if (archetype.explosiveTrigger) {
      entity.add(
        new ExplosiveTrigger({ name: `${archetype.name}#${entity.id}`, ...archetype.explosiveTrigger })
      );
    }

    if (archetype.weapon) {
      entity.add(
        Weapon.fromArchetype(archetype.weapon, enemyAmmoOverrides(archetype.weapon)).equip(entity)
      );
    }
  }

  if (archetype.explosive) {
    entity.add(
      new Explosive({
        radius: archetype.explosive.radius,
        damage: archetype.explosive.damage,
        // Blasts hurt the player and other enemies alike.
        mask: Layer.PLAYER | Layer.ENEMY
      })
    );
  }

  // `ENEMY_BULLET` is in the mask because collision is a mutual test: an enemy
  // round declaring `Layer.ENEMY` as a target is only half the deal, the target
  // has to accept the round's layer too (issue #75).
  entity.add(
    Collider.fromRadius(
      archetype.radius,
      Layer.ENEMY,
      Layer.WALL | Layer.PLAYER | Layer.ENEMY | Layer.PLAYER_BULLET | Layer.ENEMY_BULLET
    )
  );

  entity.add(
    new Renderable(Prefab.enemy(archetypeKey), meshFactory.acquire(Prefab.enemy(archetypeKey)), {
      yOffset: archetype.height * 0.55,
      proneYOffset: archetype.radius
    })
  );

  // Metadata the wave director and the drop table read off the entity.
  entity.archetypeKey = archetypeKey;
  entity.scoreValue = archetype.scoreValue;
  entity.dropBonus = archetype.dropBonus ?? 0;
  entity.despawnSeconds = archetype.despawnSeconds ?? 0;

  return entity;
}
